/**
 * Country Initializer Component
 *
 * Initializes the country store on app mount and shows the country selector
 * modal on first visit. Uses useLayoutEffect to ensure the modal state is
 * set before the first render to avoid flash of content.
 *
 * This component should be placed at the app root level (inside providers
 * but outside of routes).
 *
 * @module features/country/components/CountryInitializer
 */
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { useCountryStore } from '../hooks/useCountryStore';
import { getApiEndpoint } from '@/shared/config/environment';
import { useCountryModalActions } from '../hooks';
import { fetchCountries } from '../api/countryData';

interface GeoDetectionResponse {
  success: boolean;
  country_code?: string | null;
  country_name?: string | null;
  currency_code?: string | null;
  auto_detected?: boolean;
  detail?: string;
  message?: string;
}

/** Session storage key to track if country mismatch toast was dismissed */
const COUNTRY_MISMATCH_DISMISSED_KEY = 'tangi_country_mismatch_dismissed';

// Zod schema for country mismatch dismissal data
const MismatchDismissalSchema = z.object({
  detected: z.string(),
  current: z.string(),
});

/**
 * Check if the country mismatch toast was already shown this session
 */
function wasMismatchDismissed(detectedCode: string, currentCode: string): boolean {
  try {
    const dismissed = sessionStorage.getItem(COUNTRY_MISMATCH_DISMISSED_KEY);
    if (!dismissed) return false;

    // Parse and validate with Zod to ensure data integrity
    const parsed: unknown = JSON.parse(dismissed);
    const validationResult = MismatchDismissalSchema.safeParse(parsed);

    if (!validationResult.success) {
      // Invalid data, remove it
      sessionStorage.removeItem(COUNTRY_MISMATCH_DISMISSED_KEY);
      return false;
    }

    const data = validationResult.data;
    return data.detected === detectedCode && data.current === currentCode;
  } catch {
    return false;
  }
}

/**
 * Mark the country mismatch toast as dismissed for this session
 */
function markMismatchDismissed(detectedCode: string, currentCode: string): void {
  try {
    sessionStorage.setItem(
      COUNTRY_MISMATCH_DISMISSED_KEY,
      JSON.stringify({ detected: detectedCode, current: currentCode })
    );
  } catch {
    // Ignore storage errors
  }
}

const isGeoDetectionResponse = (value: unknown): value is GeoDetectionResponse => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  if (!('success' in value) || typeof (value as Record<'success', unknown>).success !== 'boolean') {
    return false;
  }

  return true;
};

/**
 * CountryInitializer
 *
 * Runs once on mount to check localStorage for existing country selection.
 * Detects country from IP and manages the initial country state.
 *
 * @returns null - This component renders nothing
 */
export function CountryInitializer(): null {
  const { t } = useTranslation();
  const hasInitialized = useRef(false);
  const initializeFromStorage = useCountryStore((state) => state.initializeFromStorage);
  const applyAutoDetectedCountry = useCountryStore((state) => state.applyAutoDetectedCountry);
  const setDetectionPending = useCountryStore((state) => state.setDetectionPending);
  const setDetectionError = useCountryStore((state) => state.setDetectionError);
  const setCountry = useCountryStore((state) => state.setCountry);
  const { open: openCountryModal } = useCountryModalActions();

  useLayoutEffect(() => {
    // Only initialize once per app lifecycle
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    initializeFromStorage();

    // Pre-fetch country data from API (non-blocking)
    void fetchCountries();
  }, [initializeFromStorage]);

  useEffect(() => {
    let isMounted = true;

    const detectCountry = async (): Promise<void> => {
      setDetectionPending();

      try {
        const response = await fetch(getApiEndpoint('geo'), {
          credentials: 'include',
        });
        const json = (await response.json()) as unknown;
        if (!isGeoDetectionResponse(json)) {
          throw new Error(t('ui.country.unableToDetect'));
        }

        if (!response.ok || !json.success) {
          throw new Error(json.detail ?? json.message ?? t('ui.country.unableToDetect'));
        }

        if (!isMounted) {
          return;
        }

        // Apply to store (updates auto-detected fields, and countryCode if auto source)
        const detectedCountryCode = typeof json.country_code === 'string' ? json.country_code : null;
        if (detectedCountryCode !== null) {
          applyAutoDetectedCountry({
            countryCode: detectedCountryCode,
            countryName: json.country_name ?? null,
            currencyCode: json.currency_code ?? null,
            autoDetected: Boolean(json.auto_detected),
            message: json.message ?? null,
          });
        }

        // Show feedback to user
        const store = useCountryStore.getState();
        const detectedCode =
          typeof json.country_code === 'string' ? json.country_code : null;
        const currentCode = store.countryCode;
        const isManual = store.countrySource === 'manual' || store.countrySource === 'checkout';

        // Case 1: User is manually set to X, but detected in Y -> Suggest switch
        // Only show once per session to avoid annoying the user
        if (isManual && currentCode && detectedCode && detectedCode !== currentCode) {
          // Check if we already showed this toast this session
          if (!wasMismatchDismissed(detectedCode, currentCode)) {
            // Mark as dismissed immediately so it doesn't show again on navigation
            markMismatchDismissed(detectedCode, currentCode);

            toast(`Shopping in ${store.countryName || currentCode}`, {
              description: `Switch to ${json.country_name}?`,
              action: {
                label: t('ui.country.switch'),
                onClick: () => {
                  setCountry(detectedCode, {
                    source: 'manual',
                    name: json.country_name ?? null,
                    currency: json.currency_code ?? null,
                  });
                  toast.success(t('ui.country.switchedTo', { country: json.country_name }), {
                    duration: 2000,
                  });
                },
              },
              duration: 6000,
            });
          }
        }
        // Case 2: First time or auto-update -> Welcome message (only show once per session)
        else if (
          !store.hasExplicitSelection ||
          (store.countrySource === 'auto' && detectedCode && detectedCode === currentCode)
        ) {
          // Only show if we actually have a valid detection AND haven't shown welcome this session
          const welcomeShownKey = 'tangi_welcome_shown';
          const welcomeShown = sessionStorage.getItem(welcomeShownKey);

          if (json.country_name && !welcomeShown) {
            sessionStorage.setItem(welcomeShownKey, 'true');
            toast(`Welcome! Shopping in ${json.country_name}`, {
              description: 'Prices in local currency',
              action: {
                label: t('ui.country.change'),
                onClick: () => openCountryModal(),
              },
              duration: 4000,
            });
          }
        }

      } catch (error) {
        if (!isMounted) {
          return;
        }
        setDetectionError(error instanceof Error ? error.message : t('ui.country.unableToDetect'));
      }
    };

    void detectCountry();

    return () => {
      isMounted = false;
    };
  }, [applyAutoDetectedCountry, openCountryModal, t, setCountry, setDetectionError, setDetectionPending]);

  return null;
}
